import type { RootState } from '~/store/slices';
import type { BgColor, KeyedRegion, RemoveBgOpts } from '~/types/pipeline';

let cacheKey: { regions: readonly KeyedRegion[]; w: number; h: number } | null = null;
let cached: Uint8Array | null = null;

/** Union of all region masks in source-image space. Returns null when there are
 *  no regions (cheap fast path) or when source dims are unknown. Memoized on
 *  the `regions` array reference and source dimensions. */
export function selectRegionEffectMask(state: RootState): Uint8Array | null {
  const { regions } = state.background;
  const dims = state.source.dimensions;
  if (regions.length === 0 || !dims) return null;
  const { w, h } = dims;
  if (cacheKey && cacheKey.regions === regions && cacheKey.w === w && cacheKey.h === h) {
    return cached;
  }
  const mask = new Uint8Array(w * h);
  for (const r of regions) {
    const { bbox, mask: rm } = r;
    const x0 = Math.max(0, bbox.x);
    const y0 = Math.max(0, bbox.y);
    const x1 = Math.min(w, bbox.x + bbox.w);
    const y1 = Math.min(h, bbox.y + bbox.h);
    for (let y = y0; y < y1; y++) {
      const rowBase = y * w;
      const localBase = (y - bbox.y) * bbox.w - bbox.x;
      for (let x = x0; x < x1; x++) {
        const mv = rm[localBase + x] ?? 0;
        if (mv > 0) {
          const current = mask[rowBase + x] ?? 0;
          mask[rowBase + x] = Math.max(current, mv);
        }
      }
    }
  }
  cacheKey = { regions, w, h };
  cached = mask;
  return mask;
}

export function isColorKeyActive(
  enabled: boolean,
  bgColors: readonly BgColor[],
  tolerance: number,
): boolean {
  return enabled && bgColors.length > 0 && tolerance > 0;
}

/** Build the `removeBackground` opts from current bg-state + a regionMask, or
 *  return null when neither pass would do any work. The hook variant must pass
 *  its own deferred `tolerance`; everything else mirrors the slice. */
export function buildRemoveBgOpts(
  bg: {
    enabled: boolean;
    bgColors: readonly BgColor[];
    tolerance: number;
    hardEdge: boolean;
    erode: number;
    feather: number;
    despillStrength: number;
    despillRadius: number;
    floodFill: boolean;
    maxEnclosedIsland: number;
  },
  regionMask: Uint8Array | null,
): RemoveBgOpts | null {
  const colorKeyActive = isColorKeyActive(bg.enabled, bg.bgColors, bg.tolerance);
  if (!colorKeyActive && !regionMask) return null;
  return {
    bgColors: colorKeyActive ? bg.bgColors : [],
    tolerance: bg.tolerance,
    hardEdge: bg.hardEdge,
    erode: bg.erode,
    feather: bg.feather,
    despillStrength: bg.despillStrength,
    despillRadius: bg.despillRadius,
    floodFill: bg.floodFill,
    maxEnclosedIsland: bg.maxEnclosedIsland,
    regionMask,
  };
}
